// VisitorTracker.js — tracks site visits via Cloud Function (no direct Firebase writes)

const CLOUD_FUNCTION_URL = process.env.NODE_ENV === 'production'
  ? 'https://us-central1-balance-57a74.cloudfunctions.net/trackSiteVisit'
  : 'http://localhost:5001/balance-57a74/us-central1/trackSiteVisit';

class VisitorTracker {
  constructor() {
    this._initialized = false;
  }

  /**
   * Initialize and track a site visit
   * @param {string} source - Traffic source (e.g., 'direct', 'google', etc.)
   * @returns {Promise} - A promise that resolves when tracking is complete
   */
  async trackVisit(source = null) {
    try {
      // Detect source if not provided
      if (!source) {
        source = this._detectSource();
      }

      // Create or retrieve visitor ID
      let visitorId = localStorage.getItem('visitorId');
      const isReturningVisitor = !!visitorId;

      if (!visitorId) {
        visitorId = 'v_' + Math.random().toString(36).substring(2, 15);
        localStorage.setItem('visitorId', visitorId);
      }

      // Check for owner exclusion flag (set once in browser console)
      if (localStorage.getItem('excludeFromTracking') === 'true') {
        console.log("Owner excluded from tracking");
        return;
      }

      // Check for excluded user IDs (Rainier)
      const user = window.firebase?.auth()?.currentUser;
      if (user && this._shouldExcludeUser(user.uid)) {
        console.log("Excluded user visit not tracked:", user.uid);
        return;
      }

      // Check if this is a new session (30 min timeout)
      const lastVisitTime = localStorage.getItem('lastVisitTime');
      const currentTime = Date.now();
      const sessionTimeout = 30 * 60 * 1000;

      const isNewSession = !lastVisitTime || (currentTime - parseInt(lastVisitTime)) > sessionTimeout;

      if (isNewSession) {
        const visitData = {
          visitorId,
          source,
          referrer: document.referrer || 'direct',
          userAgent: navigator.userAgent,
          deviceType: this._getDeviceType(),
          path: window.location.pathname,
          isReturningVisitor,
          timezone: this._getTimezoneInfo(),
        };

        await fetch(CLOUD_FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(visitData),
        });

        localStorage.setItem('lastVisitTime', currentTime.toString());
        console.log("Tracked site visit:", source);
      } else {
        console.log("Visit not tracked - within session timeout period");
      }

      this._initialized = true;
    } catch (error) {
      // Silently handle errors — tracking should never break the site
      console.error("Error tracking visit:", error);
    }
  }

  /**
   * Detect the visitor's traffic source
   */
  _detectSource() {
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    if (utmSource) return utmSource;

    const referrer = document.referrer;
    if (referrer) {
      try {
        const domain = new URL(referrer).hostname;
        if (domain.includes('google')) return 'google';
        if (domain.includes('facebook.com')) return 'facebook';
        if (domain.includes('instagram.com')) return 'instagram';
        if (domain.includes('tiktok.com')) return 'tiktok';
        if (domain.includes('twitter.com') || domain.includes('x.com')) return 'twitter';
        return domain;
      } catch {
        return 'direct';
      }
    }

    return 'direct';
  }

  /**
   * Detect device type
   */
  _getDeviceType() {
    const userAgent = navigator.userAgent;
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      return /iPad|tablet|Tablet/i.test(userAgent) ? "tablet" : "mobile";
    }
    return "desktop";
  }

  /**
   * Get the user's timezone information
   */
  _getTimezoneInfo() {
    const now = new Date();
    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';

    return {
      name: timezoneName,
      friendlyName: this._getFriendlyTimezoneName(timezoneName),
      offset: now.getTimezoneOffset(),
      offsetHours: -(now.getTimezoneOffset() / 60),
      localTime: now.toLocaleTimeString(),
      abbreviation: this._getTimezoneAbbreviation(now),
    };
  }

  _getFriendlyTimezoneName(timezoneName) {
    if (!timezoneName || timezoneName === 'Unknown') return 'Unknown';
    try {
      const parts = timezoneName.split('/');
      return parts[parts.length - 1].replace(/_/g, ' ');
    } catch {
      return timezoneName;
    }
  }

  _shouldExcludeUser(userId) {
    const excludedIds = ['FDUKv9'];
    return excludedIds.some(id => userId && userId.startsWith(id));
  }

  _getTimezoneAbbreviation(date) {
    try {
      const shortFormat = date.toLocaleTimeString(undefined, { timeZoneName: 'short' });
      const timeParts = shortFormat.split(' ');
      return timeParts.length >= 2 ? timeParts[timeParts.length - 1] : '';
    } catch {
      return '';
    }
  }
}

const visitorTracker = new VisitorTracker();
export default visitorTracker;
